/**
 * DOCUMENT-LIST
 * Display a list of generated documents
 */
import ListButton from "@/components/list-button"
import { Document } from "../model/document.types"
import {
  faArrowUpRightFromSquare,
  faDownload,
  faEllipsisV,
  faFileLines,
  faLink,
  faListCheck,
  faPen,
  faTriangleExclamation
} from "@fortawesome/free-solid-svg-icons"
import ListItem from "@/components/list-item"
import Chip from "@/components/chip"
import Space from "@/components/space"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import { exportDocumentToPdf } from "../utils/exportDocumentToPdf"
import env from "@/env"
import { useEffect, useRef, useState } from "react"
import DocumentActionsModal from "./modal/DocumentActionsModal"
import DocumentExportModal from "./modal/DocumentExportModal"
import DocumentDeleteModal from "./modal/DocumentDeleteModal"
import DocumentUploadModal from "./modal/DocumentUploadModal"
import DocumentUpdateModal from "./modal/DocumentUpdateModal"
import Button from "@/components/button"
import { toast } from "react-toastify"
import SaveIcon from "@/components/save-icon"
import Tooltip from "@/components/tooltip"
import { STATUS_SAVING } from "@/redux/_status.types"
import {
  downloadUrl,
  formatDocumentDate,
  getDownloadFilename,
  getUrlIcon,
  getPublicDocumentPath,
  canOpenDocument,
  openUrl
} from "../utils/document-file.utils"

interface Props {
  autoOpenDocumentId?: string
  documents: Document[] //Already ordered: current versions first
  currentDocumentIds: string[]
  latestDuerpId?: string
  showDuerpWarning?: boolean
  isDuerpMissing?: boolean //Display a row to create the first DUERP
}

const PAPRIPACT_COLOR = "#0E5AA7"

//DUERP : color of the highest risk in the snapshot (none for uploaded files)
function getChipColor(document: Document) {
  if (document.type === "papripact") return PAPRIPACT_COLOR
  const risks: any[] = document.content?.physicalRisks ?? []
  const riskMax = risks.reduce<any>(
    (max, risk) => (!max || risk.cValueEffective > max.cValueEffective ? risk : max),
    null
  )
  return riskMax?.cValueEffectiveColor ?? null
}

//Fixed width so the labels line up whatever the chip
function DocumentTypeChip(props: { type: Document["type"]; color?: string | null; isOutlineColored?: boolean }) {
  return (
    <div className="flex w-[140px] shrink-0">
      <Chip
        icon={props.type === "duerp" ? faFileLines : faListCheck}
        color={props.color}
        isOutlineColored={props.isOutlineColored}>
        {props.type === "duerp" ? "DUERP" : "PAPRIPACT"}
      </Chip>
      <Space />
    </div>
  )
}

export default function DocumentList(props: Props) {
  const { documents } = props
  //PAPRIPACT versions are numbered within their program year (the list only holds one year), oldest = 1
  const papripactIds = documents
    .filter((document) => document.type === "papripact")
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map((document) => document.id)
  const dispatch = useAppDispatch()
  const _session = useAppSelector((state) => state._session)
  const establishment = useAppSelector(establishmentStore.selectors.active)
  const accountRisksSettings = useAppSelector((state) => state._session.accountRisksSettings)
  const populations = useAppSelector((state) => state.population.list)
  const sites = useAppSelector((state) => state.site.list)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [modal, setModal] = useState<"ACTIONS" | "EXPORT" | "DELETE" | "EDIT" | "GENERATE_DUERP" | null>(null)
  const [modalBeforeDelete, setModalBeforeDelete] = useState<"ACTIONS" | "EDIT">("ACTIONS")
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null)
  const autoOpenDone = useRef(false)

  useEffect(() => {
    const document = documents.find((document) => document.id === props.autoOpenDocumentId)
    if (!document || autoOpenDone.current) return

    autoOpenDone.current = true
    openActionsModal(document)
  }, [props.autoOpenDocumentId, documents])

  function shareDocument(document: Document) {
    if (document.url) {
      navigator.clipboard.writeText(document.url)
      toast("Lien copié dans le presse-papier.")
      return
    }

    const url = `${env.REACT_APP_URL_OBSERVER}${getPublicDocumentPath(document, props.latestDuerpId)}`

    navigator.clipboard.writeText(url)
    toast("Lien copié dans le presse-papier.")
  }

  function openActionsModal(document: Document) {
    setSelectedDocument(document)
    setModal("ACTIONS")
  }

  function openDocument(document: Document, event?: any) {
    event?.stopPropagation()
    if (!document.url) {
      window.open(getPublicDocumentPath(document, props.latestDuerpId), "_blank", "noopener,noreferrer")
    } else {
      openUrl(document.url)
      setModal(null)
    }
  }

  async function downloadDocument(document: Document, event?: any) {
    event?.stopPropagation()
    if (downloadingId) return
    setDownloadingId(document.id)
    try {
      if (document.url) {
        await downloadUrl(document.url, getDownloadFilename(document))
      } else {
        await exportDocumentToPdf(dispatch, document, {
          establishment,
          accountRisksSettings,
          populations,
          sites,
          session: _session
        })
      }
      setModal(null)
    } finally {
      setDownloadingId(null)
    }
  }

  function handleShare(document = selectedDocument, event?: any) {
    event?.stopPropagation()
    if (!document) return
    shareDocument(document)
    setModal(null)
  }

  function handleDownload(document = selectedDocument) {
    if (!document || downloadingId) return
    setSelectedDocument(document)
    setModal("EXPORT")
  }

  //Cancelling the deletion goes back to the modal it was opened from
  function openDelete(from: "ACTIONS" | "EDIT") {
    setModalBeforeDelete(from)
    setModal("DELETE")
  }

  function handleDelete() {
    setModal(null)
    setSelectedDocument(null)
  }

  return (
    <>
      {props.isDuerpMissing && (
        <ListItem>
          <div className="flex flex1 min-w-0 items-center gap-[8px] text-idt-warning-main">
            <DocumentTypeChip type="duerp" isOutlineColored />
            <Tooltip text="Aucun document unique n’a encore été enregistré.">
              <FontAwesomeIcon icon={faTriangleExclamation} aria-label="Document unique manquant" />
            </Tooltip>
            <span className="font-bold">Pas de document unique</span>
          </div>
          {/* Negative margin so the button does not make the row taller, like list-button */}
          <div className="-my-[14px]">
            <Button className="primary" onClick={() => setModal("GENERATE_DUERP")}>
              Créer le document
            </Button>
          </div>
        </ListItem>
      )}

      {documents.map((document) => {
        const isCurrent = props.currentDocumentIds.includes(document.id)
        const isHighlighted = document.id === props.latestDuerpId
        const versionNumber = papripactIds.indexOf(document.id) + 1
        const canOpen = canOpenDocument(document)
        const isOutdated = Boolean(props.showDuerpWarning && isHighlighted)
        return (
          <ListItem key={document.id} isEditable onClick={() => openActionsModal(document)}>
            <div className={`flex flex1 min-w-0 items-center gap-[8px] ${isOutdated ? "text-idt-warning-main" : ""}`}>
              <DocumentTypeChip type={document.type} color={getChipColor(document)} isOutlineColored={!isCurrent} />
              {isOutdated && (
                <Tooltip text="Le dernier document unique date de plus de 12 mois. Il est à actualiser.">
                  <FontAwesomeIcon icon={faTriangleExclamation} aria-label="Document unique à actualiser" />
                </Tooltip>
              )}
              {isCurrent ? (
                <span className="font-bold">
                  Version actuelle
                  {/* Version number only once there are several versions */}
                  {document.type === "papripact" && versionNumber > 1 && ` (v${versionNumber})`}
                </span>
              ) : (
                <span className="grey-t">
                  {document.type === "papripact"
                    ? `Version ${versionNumber}`
                    : `Version du ${formatDocumentDate(document.date)}`}
                </span>
              )}
              {isCurrent && document.type === "duerp" && (
                <span className={isOutdated ? "" : "grey-t"}>
                  <span className="mr-[8px]">-</span>créé le {formatDocumentDate(document.date)}
                </span>
              )}
              {document.url && (
                <FontAwesomeIcon className={`shrink-0 ${isOutdated ? "" : "grey-t"}`} icon={getUrlIcon(document.url)} />
              )}
            </div>
            <div className="flex shrink-0 items-center">
              <SaveIcon status={downloadingId === document.id ? STATUS_SAVING : null} />
              {isOutdated && (
                // Negative margin so the button does not make the row taller, like list-button
                <div className="-my-[14px]">
                  <Button
                    className="primary"
                    onClick={(event) => {
                      event.stopPropagation()
                      setModal("GENERATE_DUERP")
                    }}>
                    Mettre à jour
                  </Button>
                </div>
              )}
              {canOpen ? (
                <ListButton
                  icon={faArrowUpRightFromSquare}
                  isFat={isHighlighted && !isOutdated}
                  text="Ouvrir"
                  onClick={() => openDocument(document)}
                />
              ) : (
                <ListButton
                  icon={faDownload}
                  isFat={isHighlighted && !isOutdated}
                  text={downloadingId === document.id ? "Téléchargement en cours" : "Télécharger"}
                  isInactive={Boolean(downloadingId)}
                  onClick={() => handleDownload(document)}
                />
              )}
              <ListButton
                icon={faEllipsisV}
                actions={[
                  ...(canOpen ? [{ id: "OPEN", icon: faArrowUpRightFromSquare, label: "Ouvrir" }] : []),
                  { id: "DOWNLOAD", icon: faDownload, label: "Télécharger", isInactive: Boolean(downloadingId) },
                  { id: "SHARE", icon: faLink, label: "Partager le lien" },
                  { id: "EDIT", icon: faPen, label: "Modifier" }
                ]}
                onAction={(actionId: string) => {
                  if (actionId === "OPEN") openDocument(document)
                  if (actionId === "DOWNLOAD") handleDownload(document)
                  if (actionId === "SHARE") handleShare(document)
                  if (actionId === "EDIT") {
                    setSelectedDocument(document)
                    setModal("EDIT")
                  }
                }}
              />
            </div>
          </ListItem>
        )
      })}

      {modal === "ACTIONS" && selectedDocument && (
        <DocumentActionsModal
          document={selectedDocument}
          onClose={() => setModal(null)}
          onOpen={() => selectedDocument && openDocument(selectedDocument)}
          onShare={handleShare}
          onDownload={handleDownload}
          onDelete={() => openDelete("ACTIONS")}
          onEdit={() => setModal("EDIT")}
        />
      )}

      {modal === "GENERATE_DUERP" && <DocumentUpdateModal onClose={() => setModal(null)} />}

      {modal === "EXPORT" && selectedDocument && (
        <DocumentExportModal
          document={selectedDocument}
          onClose={() => setModal(null)}
          onExportPdf={() => {
            setModal(null)
            downloadDocument(selectedDocument)
          }}
        />
      )}

      {modal === "EDIT" && selectedDocument && (
        <DocumentUploadModal
          document={selectedDocument}
          onClose={() => setModal(null)}
          onDelete={() => openDelete("EDIT")}
        />
      )}

      {modal === "DELETE" && selectedDocument && (
        <DocumentDeleteModal
          document={selectedDocument}
          onClose={() => setModal(modalBeforeDelete)}
          onDelete={handleDelete}
        />
      )}
    </>
  )
}
